/**
 * 配信シリーズ詳細ページ（設計書 9.2 / html 図5）。
 *
 * 設計書: `docs/design-docs/deliveries/open-click-analytics.md`。
 * 本文中の `設計書 N.M` はこの md、`html N.M` / `html 図N` は同ディレクトリの
 * `open-click-analytics.html` を指す。
 */
import { EmailConnectionRequiredBanner } from "components/EmailConnectionRequiredBanner";
import { Button } from "components/ui/Button";
import { Loading } from "components/ui/Loading/Loading";
import { useDeliveriesApi } from "hooks/use-delivery-api";
import {
  type DeliverySeries,
  type DeliverySeriesAnalytics,
  type SeriesDeliveryBreakdown,
  useDeliverySeries,
  useDeliverySeriesAnalytics,
} from "hooks/use-delivery-series-api";
import { useActiveGmailConnection } from "hooks/use-email-integrations-api";
import {
  type ReplyEmailItem,
  replyStatusDotClass,
  useReplies,
} from "hooks/use-email-replies-api";
import { cn } from "lib/utils";
import {
  ArrowLeft,
  Briefcase,
  ChevronRight,
  ExternalLink,
  Pencil,
  RefreshCw,
  User as UserIcon,
} from "lucide-react";
import { type FC, useState } from "react";
import { Link, useLocation, useNavigate, useParams } from "react-router-dom";
import { deliveryBackState } from "routes/DeliveryPage/delivery-back";
import { DeliverySeriesFormModal } from "routes/DeliverySeriesPage/components/DeliverySeriesFormModal";
import { DeliverySeriesStatusBadge } from "routes/DeliverySeriesPage/components/DeliverySeriesStatusBadge";
import { SeriesComparisonChart } from "routes/DeliverySeriesPage/components/SeriesComparisonChart";
import { formatCount, formatRate } from "utils/delivery-analytics";
import { formatShortDateTime, timeAgo } from "utils/relative-time";

/**
 * 配信シリーズ詳細ページ.
 * @return {JSX.Element} ページ
 */
export const DeliverySeriesDetailPage: FC = () => {
  const { id } = useParams<{ id: string }>();
  const seriesId = id ?? null;
  const { series, loading, refetch } = useDeliverySeries(seriesId);
  const { analytics } = useDeliverySeriesAnalytics(seriesId);
  const [editing, setEditing] = useState(false);

  if (!seriesId) return null;
  if (loading) {
    return (
      <div className="flex justify-center py-10">
        <Loading />
      </div>
    );
  }
  if (!series) {
    return (
      <div className="p-6 text-sm text-base-content/60">
        シリーズが見つかりません
        <div className="mt-3">
          <Link
            to="/delivery-series"
            className="text-primary hover:underline inline-flex items-center gap-1"
          >
            <ArrowLeft size={14} aria-hidden />
            一覧へ戻る
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6 p-6 max-w-6xl">
      <BackLink />
      <SeriesHeader series={series} onEdit={() => setEditing(true)} />
      <SummarySection series={series} analytics={analytics} />
      {analytics && analytics.deliveries.length > 0 && (
        <section className="rounded-lg border border-slate-200 bg-white shadow-sm overflow-hidden">
          <div className="px-4 py-3 border-b border-slate-200">
            <h2 className="text-sm font-semibold">配信ごとの比較</h2>
          </div>
          <div className="p-4">
            <SeriesComparisonChart deliveries={analytics.deliveries} />
          </div>
        </section>
      )}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <DeliveryHistorySection
          series={series}
          breakdowns={analytics?.deliveries ?? []}
        />
        <ReplyOverviewSection series={series} />
      </div>

      <DeliverySeriesFormModal
        isOpen={editing}
        mode="edit"
        series={series}
        onClose={() => setEditing(false)}
        onSaved={() => {
          setEditing(false);
          refetch();
        }}
      />
    </div>
  );
};

// ============================================================
// Sub-sections
// ============================================================

const BackLink: FC = () => (
  <Link
    to="/delivery-series"
    className="text-sm text-base-content/60 hover:text-base-content inline-flex items-center gap-1"
  >
    <ArrowLeft size={14} aria-hidden />
    シリーズ一覧へ
  </Link>
);

type SeriesHeaderProps = {
  series: DeliverySeries;
  onEdit: () => void;
};

const SeriesHeader: FC<SeriesHeaderProps> = ({ series, onEdit }) => {
  return (
    <header className="space-y-2">
      <div className="flex items-start gap-3 flex-wrap">
        <div className="flex-1 min-w-0">
          <h1 className="text-2xl font-bold inline-flex items-center gap-2">
            {series.name}
            <DeliverySeriesStatusBadge status={series.status} />
          </h1>
          {series.description && (
            <p className="text-sm text-base-content/70 mt-1 whitespace-pre-line">
              {series.description}
            </p>
          )}
          {(series.talentName || series.focusMatterTitle) && (
            <div className="mt-2 flex flex-wrap gap-x-4 gap-y-1 text-xs text-violet-700">
              {series.talentName && (
                <span className="inline-flex items-center gap-1">
                  <UserIcon size={12} aria-hidden />
                  タレント: {series.talentName}
                </span>
              )}
              {series.focusMatterTitle && (
                <span className="inline-flex items-center gap-1">
                  <Briefcase size={12} aria-hidden />
                  注力案件: {series.focusMatterTitle}
                </span>
              )}
            </div>
          )}
        </div>
        <Button type="button" size="sm" variant="outline" onClick={onEdit}>
          <span className="inline-flex items-center gap-1">
            <Pencil size={14} aria-hidden />
            編集
          </span>
        </Button>
      </div>
    </header>
  );
};

/**
 * 率の KPI 3 枚。**開封・クリックとは分母が違う**ので返信だけ別の行になる
 * （開封・クリックは「計測済み配信の延べ到達」、返信は「全配信の延べ到達」）。
 * 3 枚が同じ形をしているので、違うところだけを表にして 1 つの描画で回す。
 *
 * `rate` が null（A2 が無い / 計測済み配信が無い）のときは一覧 API の率に落とす。
 */
const RATE_STATS: ReadonlyArray<{
  label: string;
  /** A2 の率（無ければ null） */
  rate: (analytics: DeliverySeriesAnalytics | null) => number | null;
  /** A2 が使えないときに落とす一覧 API の率 */
  fallbackRate: (stats: DeliverySeries["stats"]) => number | null;
  /** 補助行。A2 があるときだけ呼ぶので分岐は要らない */
  sub: (analytics: DeliverySeriesAnalytics) => string;
}> = [
  {
    label: "延べ開封率",
    rate: a => a?.openRate ?? null,
    fallbackRate: s => s.openRate,
    sub: a =>
      `${formatCount(a.uniqueOpens)} / ${formatCount(a.measuredDeliveredTotal)} · 計測済み ${a.measuredDeliveryCount} 配信`,
  },
  {
    label: "延べクリック率",
    rate: a => a?.clickRate ?? null,
    fallbackRate: s => s.clickRate,
    sub: a =>
      `${formatCount(a.uniqueClicks)} / ${formatCount(a.measuredDeliveredTotal)} · 計測済み ${a.measuredDeliveryCount} 配信`,
  },
  {
    label: "延べ返信率",
    rate: a => a?.replyRate ?? null,
    fallbackRate: s => s.replyRate,
    sub: a =>
      `${formatCount(a.replyRecipients)} / ${formatCount(a.deliveredTotal)} · 全 ${a.deliveryCount} 配信`,
  },
];

type SummarySectionProps = {
  series: DeliverySeries;
  analytics: DeliverySeriesAnalytics | null;
};

/**
 * シリーズのサマリー。上段 = 配信の成績 / 下段 = 返信の対応状況 (設計書 9.2)。
 * @param {SummarySectionProps} props シリーズと A2 の集計
 * @return {JSX.Element} サマリー
 */
const SummarySection: FC<SummarySectionProps> = ({ series, analytics }) => {
  const stats = series.stats;
  const responseRate = Math.round(stats.responseRate * 100);

  return (
    <section className="rounded-lg border border-slate-200 bg-white shadow-sm p-4">
      <h2 className="text-sm font-semibold mb-3">サマリー</h2>
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4">
        <Stat label="配信回数" value={stats.deliveryCount} />
        <Stat label="延べ宛先" value={formatCount(stats.totalRecipients)} />
        <Stat
          label="延べ到達"
          value={formatCount(analytics?.deliveredTotal ?? stats.deliveredTotal)}
        />
        {RATE_STATS.map(stat => (
          <Stat
            key={stat.label}
            label={stat.label}
            value={formatRate(stat.rate(analytics) ?? stat.fallbackRate(stats))}
            // 補助行（分子 / 分母 · 母数の但し書き）は A2 があるときだけ出す
            sub={analytics ? stat.sub(analytics) : undefined}
          />
        ))}
      </div>
      <div className="mt-4 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4">
        <Stat label="受信" value={`${formatCount(stats.totalReplies)} 通`} />
        <Stat label="未対応" value={stats.unresponded} tone="error" />
        <Stat label="対応中" value={stats.inProgress} tone="warning" />
        <Stat label="完了" value={stats.completed} />
        <Stat label="対応率" value={`${responseRate}%`} />
      </div>
      <div className="mt-3 text-xs text-base-content/60">
        対応率 {stats.completed} / {stats.totalReplies}
        {stats.lastSentAt && (
          <>
            {"　·　"}最終配信: {formatShortDateTime(stats.lastSentAt)}
          </>
        )}
      </div>
    </section>
  );
};

type StatProps = {
  label: string;
  value: number | string;
  /** 対応状況のラベル側だけを着色する (数値には色を付けない) */
  tone?: "error" | "warning";
  sub?: string;
};

const STAT_TONE_COLOR: Record<NonNullable<StatProps["tone"]>, string> = {
  error: "text-rose-600 font-bold",
  warning: "text-amber-600 font-bold",
};

/**
 * サマリーの 1 項目。率の数値には色を付けない (設計書 9.0)。
 * @param {StatProps} props ラベル・値・補助行
 * @return {JSX.Element} 項目
 */
const Stat: FC<StatProps> = ({ label, value, tone, sub }) => {
  return (
    <div>
      <div
        className={cn(
          "text-xs text-base-content/60",
          tone && STAT_TONE_COLOR[tone],
        )}
      >
        {label}
      </div>
      <div className="text-xl font-bold text-base-content">{value}</div>
      {sub && <div className="text-[11px] text-slate-500">{sub}</div>}
    </div>
  );
};

type DeliveryHistorySectionProps = {
  series: DeliverySeries;
  /** A2 の配信ごとの内訳 (率の出所。一覧 API は拡張しない) */
  breakdowns: SeriesDeliveryBreakdown[];
};

/**
 * 配信履歴。各行の補助行に到達数・開封率・クリック率・返信率を追記する (設計書 9.2)。
 * @param {DeliveryHistorySectionProps} props シリーズと配信ごとの内訳
 * @return {JSX.Element} 配信履歴
 */
const DeliveryHistorySection: FC<DeliveryHistorySectionProps> = ({
  series,
  breakdowns,
}) => {
  const navigate = useNavigate();
  // 配信詳細の「戻る」がこのシリーズ詳細へ戻れるよう、リンクに載せる
  const backState = deliveryBackState(useLocation());
  const { data, loading } = useDeliveriesApi({
    seriesId: series.id,
    limit: 20,
  });
  const items = data?.items ?? [];
  const latestSentId = items.find(d => d.status === "sent")?.id ?? null;

  /**
   * 直近の配信を雛型に再配信フローへ
   * @return {void}
   */
  const handleResendLatest = (): void => {
    if (latestSentId) {
      navigate(`/deliveries/new?copy-from=${latestSentId}`);
    } else {
      navigate(`/deliveries/new?seriesId=${series.id}`);
    }
  };

  return (
    <section className="rounded-lg border border-slate-200 bg-white shadow-sm overflow-hidden">
      <div className="flex items-center justify-between px-4 py-3 border-b border-slate-200">
        <h2 className="text-sm font-semibold">配信履歴</h2>
        <Button
          type="button"
          size="xs"
          variant="outline"
          onClick={handleResendLatest}
          disabled={series.status === "archived"}
          title={
            series.status === "archived"
              ? "アーカイブ済みシリーズには新規配信できません"
              : undefined
          }
        >
          <span className="inline-flex items-center gap-1">
            <RefreshCw size={12} aria-hidden />
            {latestSentId ? "再配信 (直近)" : "新規配信"}
          </span>
        </Button>
      </div>
      {loading ? (
        <div className="flex justify-center py-6">
          <Loading size="sm" />
        </div>
      ) : items.length === 0 ? (
        <div className="px-4 py-6 text-sm text-base-content/60">
          このシリーズにはまだ配信がありません
        </div>
      ) : (
        <ul>
          {items.slice(0, 10).map(d => (
            <li
              key={d.id}
              className="px-4 py-3 border-b border-slate-100 last:border-b-0 hover:bg-slate-50"
            >
              <Link
                to={`/deliveries/${d.id}`}
                state={backState}
                className="flex items-center gap-2 text-sm"
              >
                <div className="flex-1 min-w-0">
                  <div className="font-medium truncate">
                    {d.subject || "(件名なし)"}
                  </div>
                  <div className="text-xs text-base-content/60">
                    {d.sentAt
                      ? `配信 ${formatShortDateTime(d.sentAt)}`
                      : `下書き / 予約 (${d.status})`}{" "}
                    · {d.totalRecipients} 名宛
                    <DeliveryRates
                      breakdown={breakdowns.find(b => b.deliveryId === d.id)}
                      sent={!!d.sentAt}
                    />
                  </div>
                </div>
                <ChevronRight
                  size={14}
                  className="text-base-content/40 shrink-0"
                  aria-hidden
                />
              </Link>
            </li>
          ))}
          {items.length > 10 && (
            <li className="px-4 py-2 text-xs text-base-content/50">
              …他 {items.length - 10} 件
            </li>
          )}
        </ul>
      )}
    </section>
  );
};

type DeliveryRatesProps = {
  breakdown: SeriesDeliveryBreakdown | undefined;
  sent: boolean;
};

/**
 * 配信履歴 1 行の率の追記。A2 に無い配信は「—」を出す (設計書 9.2)。
 * @param {DeliveryRatesProps} props 配信ごとの内訳
 * @return {JSX.Element | null} 追記
 */
const DeliveryRates: FC<DeliveryRatesProps> = ({ breakdown, sent }) => {
  if (!sent) return null;
  if (!breakdown) {
    return <> · 到達 — · 開封 — クリック — 返信 —</>;
  }
  return (
    <>
      {" "}
      · 到達 {formatCount(breakdown.delivered)} · 開封{" "}
      {formatRate(breakdown.openRate)}
      {"　"}クリック {formatRate(breakdown.clickRate)}
      {"　"}返信 {formatRate(breakdown.replyRate)}
    </>
  );
};

type ReplyOverviewSectionProps = {
  series: DeliverySeries;
};

const ReplyOverviewSection: FC<ReplyOverviewSectionProps> = ({ series }) => {
  const navigate = useNavigate();
  const { hasActiveConnection, loading: connectionLoading } =
    useActiveGmailConnection();
  const { items: replies, loading } = useReplies({
    seriesId: series.id,
    limit: 10,
  });

  return (
    <section className="rounded-lg border border-slate-200 bg-white shadow-sm overflow-hidden flex flex-col">
      <div className="px-4 py-3 border-b border-slate-200">
        <h2 className="text-sm font-semibold">
          リプライ一覧 (このシリーズ全体)
        </h2>
      </div>
      {!connectionLoading && !hasActiveConnection && (
        <div className="p-4">
          <EmailConnectionRequiredBanner message="Gmail を連携するとリプライが自動収集されます。" />
        </div>
      )}
      {loading ? (
        <div className="flex justify-center py-6">
          <Loading size="sm" />
        </div>
      ) : replies.length === 0 ? (
        <div className="px-4 py-6 text-sm text-base-content/60">
          このシリーズにはまだリプライがありません
        </div>
      ) : (
        <ul className="flex-1">
          {replies.slice(0, 6).map(r => (
            <ReplyOverviewRow key={r.id} reply={r} />
          ))}
        </ul>
      )}
      <div className="border-t border-slate-200 px-4 py-3">
        <Button
          type="button"
          size="sm"
          onClick={() => navigate(`/emails/replies?seriesId=${series.id}`)}
        >
          <span className="inline-flex items-center gap-1">
            <ExternalLink size={14} aria-hidden />
            リプライ管理で開く ({series.stats.totalReplies} 件)
          </span>
        </Button>
      </div>
    </section>
  );
};

type ReplyOverviewRowProps = {
  reply: ReplyEmailItem;
};

const ReplyOverviewRow: FC<ReplyOverviewRowProps> = ({ reply }) => {
  return (
    <li className="px-4 py-2 border-b border-slate-100 last:border-b-0">
      <div className="flex items-center gap-2">
        <span
          className={cn(
            "inline-block w-2 h-2 rounded-full shrink-0",
            replyStatusDotClass(reply.responseStatus),
          )}
          title={`status:${reply.responseStatus}`}
        />
        <span className="text-sm font-medium truncate">
          {reply.fromName || reply.fromEmail}
        </span>
        <span className="ml-auto text-xs text-base-content/50 shrink-0">
          {timeAgo(reply.receivedAt)}
        </span>
      </div>
      <div className="text-xs text-base-content truncate ml-4">
        {reply.subject || "(件名なし)"}
      </div>
    </li>
  );
};
